/**
 * The placement flow across the stores: confirm (add the antenna to the
 * dataset, re-run the what-if analysis, select it, fly to it, toast) and
 * remove. The globe engine is passed in (getGlobeEngine() at the call site) so
 * this module never imports Cesium.
 */
import { useUI } from '@/state/store';
import { toast } from '@/components/ui/toast';
import type { GlobeEngine } from '../globe/GlobeEngine';
import { useLoader } from '../store';
import type { Antenna } from '../types';
import { antennaFromPlacement, placedFlyHeightM, removePlaced } from './placement';
import { usePlacement } from './store';
import { syncPlacedTwins } from './twinBridge';

/** Same duration as GlobeEngine.flyToAntenna; the height frames the new coverage circle (placedFlyHeightM). */
export const PLACED_FLY_DURATION_S = 1.5;

/** The strip's convention for the globe visibility allowlist: null once it covers every antenna. */
function visibleOrAll(ids: string[], antennaIds: readonly string[]): string[] | null {
  const listed = new Set(ids);
  return antennaIds.every((id) => listed.has(id)) ? null : ids;
}

/**
 * Build the antenna from the pending spec at the confirmed point and run the
 * what-if: its twin spec is registered, patchDataset keeps the what-if
 * selection and adds the new antenna as active, an active globe visibility
 * filter (the strip's allowlist) gains it so it is drawn, then the analysis
 * re-runs, the antenna is selected and the camera flies to it, high enough to
 * frame its coverage circle (by coordinates: the globe draws the antenna only
 * after React applies the new dataset).
 * Throws before changing the dataset when there is no point, no dataset, the
 * spec is invalid or the twin registry rejects the spec.
 */
export function commitPlacement(engine: Pick<GlobeEngine, 'flyTo'> | null): Antenna {
  const placement = usePlacement.getState();
  const target = placement.target;
  if (!target) throw new Error('commitPlacement: no ground point awaits confirmation');
  const loader = useLoader.getState();
  const dataset = loader.dataset;
  if (!dataset) throw new Error('commitPlacement: the network dataset is not loaded');

  const taken = new Set([...dataset.antennas.map((a) => a.id), ...placement.issuedIds]);
  const antenna = antennaFromPlacement(placement.pending, target.lat, target.lon, taken);
  const antennas = [...dataset.antennas, antenna];

  syncPlacedTwins(antennas);
  loader.patchDataset({ antennas });
  const visible = loader.visibleAntennaIds;
  if (visible) {
    loader.setVisibleAntennaIds(
      visibleOrAll(
        [...visible, antenna.id],
        antennas.map((a) => a.id),
      ),
    );
  }
  loader.requestAnalysis();
  usePlacement.getState().placed(antenna.id);
  useUI.getState().select({ antennaId: antenna.id, missionId: null });
  engine?.flyTo(
    antenna.location.lon,
    antenna.location.lat,
    placedFlyHeightM(antenna, dataset.missions),
    PLACED_FLY_DURATION_S,
  );
  toast('success', `Placed ${antenna.shortName} at ${antenna.location.name}; re-running the analysis.`);
  return antenna;
}

/**
 * Take a placed antenna out of the dataset (and its twin out of the registry,
 * its id out of an active visibility filter), clear it from the selection and
 * re-run. Throws for unknown or network antennas.
 */
export function removePlacedAntenna(id: string): void {
  const loader = useLoader.getState();
  const dataset = loader.dataset;
  if (!dataset) throw new Error('removePlacedAntenna: the network dataset is not loaded');
  const antenna = dataset.antennas.find((a) => a.id === id);
  const patch = removePlaced(dataset, id);
  syncPlacedTwins(patch.antennas);
  loader.patchDataset(patch);
  const visible = loader.visibleAntennaIds;
  if (visible) {
    loader.setVisibleAntennaIds(
      visibleOrAll(
        visible.filter((x) => x !== id),
        patch.antennas.map((a) => a.id),
      ),
    );
  }
  loader.requestAnalysis();
  usePlacement.getState().removed(id);
  if (useUI.getState().selection.antennaId === id) useUI.getState().select({ antennaId: null });
  toast('info', `Removed ${antenna?.shortName ?? id}; re-running the analysis.`);
}
